import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery } from '../utils/dbUtils';
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { collection, getCountFromServer, getDocs, limit, orderBy, query, where, startAfter } from 'firebase/firestore';
import { db } from '../firebase';
import User from './User'
import ConversationMessage from './ConversationMessage';
import AuditLog from './AuditLog';

class Conversation {
    static collectionName = 'conversations';

    // Basic Info
    name = "New Conversation";
    description = '';
    type = "OTHER"; // GROUP, COMMUNITY, CUSTOMER_SUPPORT, PROVIDER, ENCOUNTER, EVENT, OTHER
    topic = ''; // Current conversation topic/purpose

    // Member Management
    participantIds = []; // array of user ids who are participating
    adminIds = []; // array of user ids who are admins
    moderatorIds = []; // array of user ids who can moderate
    bannedUserIds = []; // array of user ids banned from the conversation

    // Permissions
    permissions = {
        canSendMessages: true,
        canSendMedia: true,
        canAddReactions: true,
        slowMode: false,
        slowModeInterval: 0, // time in seconds between messages
        joinRequiresApproval: false,
        whoCanMessage: 'ALL' // ALL, ADMINS_ONLY, MODERATORS_AND_ADMINS
    };

    // Message Management
    lastMessage = {}; // snapshot of the last ConversationMessage sent in the Conversation
    pinnedMessages = []; // array of ConversationMessages pinned
    messageCount = 0;
    lastActiveAt = null;

    // Media Settings
    mediaSettings = {
        allowImages: true,
        allowVideos: true,
        allowFiles: true,
        maxFileSize: 10485760, // 10MB in bytes
        allowedFileTypes: ['image/*', 'video/*', 'application/pdf']
    };

    // Metadata
    metadata = {
        communityId: null,
        communityName: null,
        communityType: null
    };
    tags = [];
    status = 'ACTIVE'; // ACTIVE, ARCHIVED, READ_ONLY, DELETED

    // Support-specific fields (only used when type = 'CUSTOMER_SUPPORT')
    supportMetadata = {
        ticketId: null,
        assignedAgentId: null,
        aiEnabled: true,
        aiHandoffReason: null,
        isAiHandling: true,
        customerUserId: null,
        customerName: null,
        customerEmail: null,
        isResolved: false,
        resolvedAt: null,
        resolvedBy: null
    };
    visibility = 'PUBLIC'; // PUBLIC, PRIVATE, HIDDEN
    testMode = false;

    communityId = null;

    constructor(conversation) {
        if (isObjectNotEmpty(conversation)) {
            if (isStringNotEmpty(conversation.name)) { this.name = conversation.name; }
            if (isStringNotEmpty(conversation.description)) { this.description = conversation.description; }
            if (isStringNotEmpty(conversation.type)) { this.type = conversation.type; }
            if (isStringNotEmpty(conversation.topic)) { this.topic = conversation.topic; }
            if (isListNotEmpty(conversation.tags)) { this.tags = conversation.tags; }

            if (isListNotEmpty(conversation.participantIds)) { this.participantIds = conversation.participantIds; }
            if (isListNotEmpty(conversation.adminIds)) { this.adminIds = conversation.adminIds; }
            if (isListNotEmpty(conversation.moderatorIds)) { this.moderatorIds = conversation.moderatorIds; }
            if (isListNotEmpty(conversation.bannedUserIds)) { this.bannedUserIds = conversation.bannedUserIds; }

            if (isObjectNotEmpty(conversation.permissions)) { this.permissions = conversation.permissions; }
            if (isObjectNotEmpty(conversation.mediaSettings)) { this.mediaSettings = conversation.mediaSettings; }
            if (isObjectNotEmpty(conversation.metadata)) { this.metadata = conversation.metadata; }
            if (isObjectNotEmpty(conversation.supportMetadata)) { this.supportMetadata = { ...this.supportMetadata, ...conversation.supportMetadata }; }

            if (isObjectNotEmpty(conversation.lastMessage)) { this.lastMessage = conversation.lastMessage; }
            if (isListNotEmpty(conversation.pinnedMessages)) { this.pinnedMessages = conversation.pinnedMessages; }
            if (conversation.messageCount) { this.messageCount = conversation.messageCount; }
            if (conversation.lastActiveAt) { this.lastActiveAt = conversation.lastActiveAt; }

            if (isStringNotEmpty(conversation.status)) { this.status = conversation.status; }
            if (isStringNotEmpty(conversation.visibility)) { this.visibility = conversation.visibility; }
            if (isBooleanTrue(conversation.testMode)) { this.testMode = true; }

            if (isStringNotEmpty(conversation.communityId)) { this.communityId = conversation.communityId; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            description: this.description,
            type: this.type,
            topic: this.topic,
            tags: this.tags,

            participantIds: this.participantIds,
            adminIds: this.adminIds,
            moderatorIds: this.moderatorIds,
            bannedUserIds: this.bannedUserIds,

            permissions: this.permissions,
            mediaSettings: this.mediaSettings,
            metadata: this.metadata,

            lastMessage: this.lastMessage,
            pinnedMessages: this.pinnedMessages,
            messageCount: this.messageCount,
            lastActiveAt: this.lastActiveAt,

            status: this.status,
            visibility: this.visibility,
            testMode: this.testMode,

            supportMetadata: this.supportMetadata,

            communityId: this.communityId
        };
    }

    isValid = () => {
        if (isListEmpty(this.participantIds)) {
            throw new Error('cConversation must have at least 1 participant.');
        }

        if (isListEmpty(this.adminIds)) {
            throw new Error('Conversation must have at least 1 admin.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(Conversation.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Conversation.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Conversation.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedConversation) => {
        const updatedObj = new Conversation(updatedConversation);
        if (updatedObj.isValid() && id === updatedConversation.id) {
            return await update(Conversation.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated conversation must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Conversation.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Conversation.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Conversation.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Conversation.collectionName, fieldName, fieldValue, comparer);
    }

    static getByCommunityId = async (communityId) => {
        return await getBySingleQuery(Conversation.collectionName, 'communityId', communityId, '==');
    }

    static getByParticipantId = async (userId) => {
        return await getBySingleQuery(Conversation.collectionName, 'participantIds', userId, 'array-contains');
    }

    static getByAdminId = async (userId) => {
        return await getBySingleQuery(Conversation.collectionName, 'adminIds', userId, 'array-contains');
    }

    static getParticipants = async (conversationId) => {
        const conversation = await this.getById(conversationId);
        return await getBySingleQuery(User.collectionName, 'userId', conversation?.participantIds, 'in')
    }

    static getAdmins = async (conversationId) => {
        const conversation = await this.getById(conversationId);
        return await getBySingleQuery(User.collectionName, 'userId', conversation?.adminIds, 'in')
    }

    static addParticipant = async (conversationId, participantIdToAdd) => {
        const conversation = await this.getById(conversationId);

        // Check if user is banned
        if (conversation.bannedUserIds?.includes(participantIdToAdd)) {
            throw new Error('User is banned from this conversation');
        }

        // Check if user is already a participant
        if (conversation.participantIds?.includes(participantIdToAdd)) {
            return true; // Already a participant, no action needed
        }

        await new AuditLog({
            type: "conversation",
            action: "addParticipant",
            state: conversation,
            targetUserId: participantIdToAdd
        }).create();

        const updatedParticipantIds = [...(conversation.participantIds || []), participantIdToAdd];
        return await updateField(Conversation.collectionName, conversationId, 'participantIds', updatedParticipantIds);
    };

    static removeParticipant = async (conversationId, participantIdToRemove, override = false) => {
        const conversation = await this.getById(conversationId);

        // Check if user is the last admin
        if (conversation.adminIds?.includes(participantIdToRemove) &&
            conversation.adminIds.length <= 1 && isBooleanFalse(override)) {
            throw new Error('Cannot remove the last admin from the conversation');
        }

        // Create audit log
        await new AuditLog({
            type: "conversation",
            action: "removeParticipant",
            state: conversation,
            targetUserId: participantIdToRemove
        }).create();

        // Remove from all roles
        const updates = {
            participantIds: conversation.participantIds?.filter(id => id !== participantIdToRemove) || [],
            moderatorIds: conversation.moderatorIds?.filter(id => id !== participantIdToRemove) || [],
            adminIds: conversation.adminIds?.filter(id => id !== participantIdToRemove) || []
        };

        // Update all fields at once
        return await updateFields(Conversation.collectionName, conversationId, updates);
    };

    static search = async (searchCriteria) => {
        let { name, tag } = searchCriteria;

        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(name)) {
            queryConstraints.push(where('name', '==', name));
        }

        if (isStringNotEmptyOrZeroLength(tag)) {
            queryConstraints.push(where('tags', 'array-contains', tag));
        }

        queryConstraints.push(orderBy('createdAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getCount = async (queryParameters) => {
        const { testMode, name, tag } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isStringNotEmptyOrZeroLength(name)) {
            queryConstraints.push(where('name', '==', name));
        }

        if (isStringNotEmptyOrZeroLength(tag)) {
            queryConstraints.push(where('tags', 'array-contains', tag));
        }

        let q = query(collection(db, Conversation.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, orderByField = 'createdAt') => {
        const { testMode, name, tag } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isStringNotEmptyOrZeroLength(name)) {
            queryConstraints.push(where('name', '==', name));
        }

        if (isStringNotEmptyOrZeroLength(tag)) {
            queryConstraints.push(where('tags', 'array-contains', tag));
        }

        const q = query(collection(db, Conversation.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));


        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await Conversation.getCount(queryParameters);
            let totalPages = Math.ceil(totalCount / 10000.0);

            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {

                let list = await Conversation.getPaginated(queryParameters, 10000, 'createdAt');

                allRecords = [...allRecords, ...list];

                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    // Enhanced member management methods
    static addModerator = async (conversationId, userId) => {
        const conversation = await this.getById(conversationId);
        if (!conversation.moderatorIds.includes(userId)) {
            conversation.moderatorIds.push(userId);
            await this.updateField(conversationId, 'moderatorIds', conversation.moderatorIds);
            await new AuditLog({
                type: "conversation",
                state: conversation,
                action: "addModerator",
                targetUserId: userId
            }).create();
        }
    }

    static banUser = async (conversationId, userId, reason) => {
        const conversation = await this.getById(conversationId);
        if (!conversation.bannedUserIds.includes(userId)) {
            // Remove from all other roles
            conversation.participantIds = conversation.participantIds.filter(id => id !== userId);
            conversation.moderatorIds = conversation.moderatorIds.filter(id => id !== userId);
            conversation.adminIds = conversation.adminIds.filter(id => id !== userId);

            // Add to banned users
            conversation.bannedUserIds.push(userId);

            await this.updateFields(conversationId, {
                participantIds: conversation.participantIds,
                moderatorIds: conversation.moderatorIds,
                adminIds: conversation.adminIds,
                bannedUserIds: conversation.bannedUserIds
            });

            await new AuditLog({
                type: "conversation",
                state: conversation,
                action: "banUser",
                targetUserId: userId,
                metadata: { reason }
            }).create();
        }
    }

    // Permission management methods
    static updatePermissions = async (conversationId, newPermissions) => {
        const conversation = await this.getById(conversationId);
        await this.updateField(conversationId, 'permissions', {
            ...conversation.permissions,
            ...newPermissions
        });
    }

    // Message management methods
    static incrementMessageCount = async (conversationId) => {
        const conversation = await this.getById(conversationId);
        await this.updateFields(conversationId, {
            messageCount: (conversation.messageCount || 0) + 1,
            lastActiveAt: new Date()
        });
    }

    // Status management methods
    static archive = async (conversationId) => {
        await this.updateField(conversationId, 'status', 'ARCHIVED');
    }

    static setReadOnly = async (conversationId) => {
        await this.updateFields(conversationId, {
            status: 'READ_ONLY',
            permissions: {
                canSendMessages: false,
                canSendMedia: false
            }
        });
    }

    // Enhanced query methods
    static getActive = async () => {
        return await getBySingleQuery(Conversation.collectionName, 'status', 'ACTIVE');
    }

    static getByVisibility = async (visibility) => {
        return await getBySingleQuery(Conversation.collectionName, 'visibility', visibility);
    }

    static getMostActive = async (limit = 10) => {
        const q = query(
            collection(db, Conversation.collectionName),
            where('status', '==', 'ACTIVE'),
            orderBy('messageCount', 'desc'),
            limit(limit)
        );
        return await getBySingleQuery(q);
    }

    static getUserConversations = async (userId) => {
        const q = query(
            collection(db, this.collectionName),
            where('participantIds', 'array-contains', userId),
            where('status', '==', 'ACTIVE'),
            orderBy('lastActiveAt', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    };
};

export default Conversation;